import React from 'react';
import { motion } from 'framer-motion';
import { FileText, Clock, Home, MapPin, Shield, Lock, CreditCard, Star } from 'lucide-react';
export function Hero() {
  return <section className="relative bg-night text-white pt-6 pb-10 overflow-hidden" data-id="element-128">
      {/* Decorative background elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-129">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-purple-500 opacity-10 blur-[120px]" data-id="element-130"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-gold-accent opacity-10 blur-[120px]" data-id="element-131"></div>
      </div>

      <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-132">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass text-gold-accent text-sm font-semibold tracking-wider mb-6" data-id="element-133">
          <span data-id="element-134">🏛️</span> BIBLIOTECAS DIGITALES™
        </motion.div>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="md:text-5xl lg:text-6xl font-bold mb-4 text-white text-[28px]" data-id="element-135">
          Todo lo que necesitás para trabajar como{' '}
          <span className="text-gold-accent italic" data-id="element-136">Secretaria Virtual.</span>
          <span className="block mt-2" data-id="element-137">Listo. Desde hoy.</span>
        </motion.h1>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} className="md:text-xl text-textLight mb-6 max-w-2xl mx-auto text-[14px]" data-id="element-138">
          El sistema N°1 para aprender una profesión digital desde cero
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.7,
        delay: 0.3
      }} className="w-full max-w-[260px] sm:max-w-xs md:max-w-sm mx-auto mb-6 relative" data-id="element-139">
          <div className="absolute inset-0 bg-gold-accent/20 blur-3xl rounded-full" data-id="element-140"></div>
          <div className="relative z-10 drop-shadow-2xl" data-id="element-141">
            <img src="https://cdn.magicpatterns.com/uploads/uVf5D2m6MkLbL5fvSACVXr/mockup_sin_fondo.png" alt="Colección Secretaria Virtual desde Cero" className="w-full h-auto object-contain" data-id="element-142" />
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="flex flex-wrap justify-center gap-3 mb-6" data-id="element-143">
          {[{
          icon: FileText,
          text: '100% PDF'
        }, {
          icon: Clock,
          text: 'Acceso en 5 min'
        }, {
          icon: Home,
          text: 'Desde casa'
        }, {
          icon: MapPin,
          text: 'Para Argentina'
        }].map((feature, idx) => <div key={idx} className="flex items-center gap-2 glass rounded-lg px-3 py-2 text-textLight text-[10px]" data-id="element-144">
              <feature.icon className="w-4 h-4 text-gold-accent" data-id="element-145" />
              {feature.text}
            </div>)}
        </motion.div>

        <motion.a href="#pricing" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.5
      }} whileHover={{
        scale: 1.02
      }} whileTap={{
        scale: 0.98
      }} className="inline-flex items-center justify-center gap-2 w-full sm:w-auto bg-gold-btn hover:bg-gold-btnHover text-night font-bold text-lg px-8 py-4 rounded-xl shadow-[0_0_30px_rgba(224,178,76,0.4)] transition-all duration-300" data-id="element-146">
          💪 QUIERO MI COLECCIÓN AHORA
        </motion.a>

        <div className="mt-6 flex flex-wrap justify-center items-center gap-4 text-sm text-textLight/80" data-id="element-147">
          <span className="flex items-center gap-1" data-id="element-148">
            <Shield className="w-4 h-4" data-id="element-149" /> Garantía 7 días
          </span>
          <span className="hidden sm:inline" data-id="element-150">•</span>
          <span className="flex items-center gap-1" data-id="element-151">
            <Lock className="w-4 h-4" data-id="element-152" /> Pago seguro
          </span>
          <span className="hidden sm:inline" data-id="element-153">•</span>
          <span className="flex items-center gap-1" data-id="element-154">
            <CreditCard className="w-4 h-4" data-id="element-155" /> Mercado Pago
          </span>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 flex flex-wrap justify-center items-center gap-6 text-sm font-medium" data-id="element-156">
          <div className="flex items-center gap-2" data-id="element-157">
            <div className="flex -space-x-2" data-id="element-158">
              {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full bg-purple-700 border-2 border-night flex items-center justify-center text-xs" data-id="element-159">
                  👩
                </div>)}
            </div>
            <span className="text-textLight" data-id="element-160">+500 personas ya empezaron</span>
          </div>
          <div className="flex items-center gap-4 text-textLight" data-id="element-161">
            <span data-id="element-162">📚 6 Tomos</span>
            <span data-id="element-163">🎁 6 Bonos</span>
            <span className="flex items-center gap-1 text-gold-accent" data-id="element-164">
              <Star className="w-4 h-4 fill-current" data-id="element-165" /> 4.9/5
            </span>
          </div>
        </div>
      </div>
    </section>;
}